/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import {
  ExpandableOverviewItem,
  CheckboxToggle,
  Button,
  TextArea,
  CurrencyInput,
  Table,
  SvgIcon,
  KeyValueTable
} from '../'

import { ReadOnlyContext } from '../../context'

import {
  showAsCurrency,
  isEmptyObject
} from '../../utils'


export default class LoanApplicationCalculations extends React.Component {
  static propTypes = {
    loanApplication: PropTypes.string.isRequired,
    loans: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      loanType: PropTypes.string,
      amount: PropTypes.number,
      balance: PropTypes.number,
      instalment: PropTypes.number,
      lmsId: PropTypes.string
    })),
    saveLoan: PropTypes.func.isRequired,
    calculations: PropTypes.object.isRequired,  // object from JSON
    cycle: PropTypes.string,
    employerLoansRules: PropTypes.string,
    cashEligibleFor: PropTypes.number,
    retailEligibleFor: PropTypes.number,
    consolidationEligibleFor: PropTypes.number,
    overrideBy: PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string
    }),
    overrideAt: PropTypes.string,
    open: PropTypes.bool.isRequired,
    updateLoanApplication: PropTypes.func.isRequired,
    refetchData: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  state = {
    cashAppliedForEligible: null,
    retailAppliedForEligible: null,
    consolidationAppliedForEligible: null,
    showDetails: false
  }

  showLoansEligibleFor = (eligibleForLoanAmountGreaterThanAppliedFor, overrideBy) => {
    let eligibleForSummary = ''
    if (this.props.cashEligibleFor) {
      eligibleForSummary += `Cash: ${showAsCurrency('R', this.props.cashEligibleFor)}`
    }
    if (this.props.retailEligibleFor) {
      eligibleForSummary += `${eligibleForSummary.length > 0 ? ', ' : ''}Retail: ${showAsCurrency('R', this.props.retailEligibleFor)}`
    }
    if (this.props.consolidationEligibleFor) {
      eligibleForSummary += `${eligibleForSummary.length > 0 ? ', ' : ''}Consolidation: ${showAsCurrency('R', this.props.consolidationEligibleFor)}`
    }
    return eligibleForSummary
  }

  isEligibleForLoansAppliedFor = (cashAppliedFor, retailAppliedFor, consolidationAppliedFor, cashEligibleFor, retailEligibleFor, consolidationEligibleFor) => (
    (cashAppliedFor && cashAppliedFor > 0 && cashEligibleFor) &&
    (retailAppliedFor && retailAppliedFor > 0 && cashEligibleFor) &&
    (consolidationAppliedFor && consolidationAppliedFor > 0 && consolidationEligibleFor)
  )

  toggleShowDetails = () => this.setState(prevState => ({ showDetails: !prevState.showDetails }))

  hasNoLimitationResults = (limitations) => (
    isEmptyObject(limitations) || Object.values(limitations).find(value => value === null)
    // 2nd condition checks whether limitation object properties are null, which would cause KeyValueTable with 'highlight' prop breakage as it will attempt to match payroll cycle against null value
  )

  render() {
    let { loanApplication, calculations, cycle, employerLoansRules, cashAppliedFor, retailAppliedFor, consolidationAppliedFor, cashEligibleFor, retailEligibleFor, consolidationEligibleFor, saveLoan, open, openRequestLoans, updateLoanApplication, overrideBy, overrideAt, onClick, className } = this.props
    let appliedForLoansEligible = this.isEligibleForLoansAppliedFor(cashAppliedFor, retailAppliedFor, consolidationAppliedFor, cashEligibleFor, retailEligibleFor, consolidationEligibleFor)
    return (
      <ExpandableOverviewItem
        title='Calculations'
        summary={this.showLoansEligibleFor(overrideBy)}
        iconSymbol={appliedForLoansEligible
          ? 'check'
          : null
        }
        iconColour={appliedForLoansEligible
          ? 'success'
          : null
        }
        open={open}
        onClick={onClick}
        className={className}>
        <div className='slds-grid slds-m-bottom_x-small'>
          Show calculation details
          <CheckboxToggle
            id='toggleShowDetails'
            checked={this.state.showDetails}
            onChange={this.toggleShowDetails}
            className='slds-m-left_large'
          />
        </div>
        {this.state.showDetails &&
          <div className='slds-theme_shade slds-p-around_small'>
            <Inputs calculationInputs={calculations.inputs} payrollCycle={cycle} />
            <Variables calculationVariables={calculations.variables} />
            <Limitations
              limitationResults={calculations.results.limitations}
              payrollCycle={cycle}
              hasNoLimitationResults={this.hasNoLimitationResults}
              refetchData={this.props.refetchData}
            />
            <Affordability
              calculationAffordability={calculations.results.affordability}
              payrollCycle={cycle}
            />
          </div>
        }
        <CalculationOutcomeSummary
          maxInstalment={this.hasNoLimitationResults(calculations.results.limitations)
            ? 0
            : calculations.results.limitations['Maximum Instalment']}
          maxIssuable={this.hasNoLimitationResults(calculations.results.limitations)
            ? 0
            : calculations.results.limitations['Maximum Value - Total Debt']
          }
          payrollCycle={cycle}
        />
        <EmployerRules rules={employerLoansRules} />
        <LoanEligibility
          loanApplication={loanApplication}
          cashEligibleFor={cashEligibleFor}
          retailEligibleFor={retailEligibleFor}
          consolidationEligibleFor={consolidationEligibleFor}
          saveLoan={saveLoan}
          openRequestLoans={openRequestLoans}
          updateLoanApplication={updateLoanApplication}
          overrideAt={overrideAt}
          overrideByName={overrideBy ? `${overrideBy.firstName} ${overrideBy.lastName}` : null}
          refetchData={this.props.refetchData}
        />
      </ExpandableOverviewItem>
    )
  }
}

const Inputs = ({ calculationInputs, payrollCycle }) => {
  let { 'Minimum Expenses Table': mET, ...rest } = calculationInputs
  return (
    <div className='slds-m-bottom_small'>
      <div className='u-font-size-larger slds-m-top_small u-font-weight-bold slds-m-bottom_small'>
        Inputs:
        <PayrollCycle cycle={payrollCycle} />
      </div>
      {isEmptyObject(calculationInputs)
        ? 'None'
        : <KeyValueTable
            data={rest}
            containsKeyValueTableObjects={true}
            currencyValues={true}
            highlight={payrollCycle
              ? payrollCycle.toLowerCase()
              : null
            }
            keyColumnSize='7-of-12'
          />
      }
    </div>
  )
}

const Variables = ({ calculationVariables }) => (
  <div className='slds-m-bottom_small'>
    <div className='u-font-size-larger slds-m-top_small u-font-weight-bold slds-m-bottom_small'>
      Variables:
    </div>
    {isEmptyObject(calculationVariables)
      ? 'None'
      : <KeyValueTable
          data={calculationVariables}
          currencyValues={true}
          keyColumnSize='7-of-12'
        />
    }
  </div>
)

const Affordability = ({ calculationAffordability, payrollCycle }) => (
  <div className='slds-m-bottom_small'>
    <div className='u-font-size-larger slds-m-top_small u-font-weight-bold slds-m-bottom_small'>
      Affordability:
      <PayrollCycle cycle={payrollCycle} />
    </div>
    {isEmptyObject(calculationAffordability)
      ? 'None'
      : <KeyValueTable
          data={calculationAffordability}
          containsKeyValueTableObjects={true}
          currencyValues={true}
          highlight={payrollCycle
            ? payrollCycle.toLowerCase()
            : null
          }
          keyColumnSize='7-of-12'
        />
    }
  </div>
)

class Limitations extends React.PureComponent {
  render() {
    let { limitationResults, payrollCycle, hasNoLimitationResults } = this.props
    return (
      <div className='slds-m-bottom_small'>
        <div className='u-font-size-larger slds-m-top_small u-font-weight-bold slds-m-bottom_small'>
          Limitations:
          <PayrollCycle cycle={payrollCycle} />
        </div>
        {hasNoLimitationResults(limitationResults)
          ? 'None'
          : <React.Fragment>
              <KeyValueTable
                data={limitationResults}
                containsKeyValueTableObjects={true}
                currencyValues={true}
                highlight={payrollCycle
                  ? payrollCycle.toLowerCase()
                  : null
                }
                keyColumnSize='7-of-12'
              />
            </React.Fragment>
        }
      </div>
    )
  }
}

const CalculationOutcomeSummary = ({ maxInstalment, maxIssuable, payrollCycle }) => (
  <div className='slds-m-bottom_small'>
    <div className='u-font-size-larger slds-m-top_small u-font-weight-bold slds-m-bottom_small'>
      Calculation outcome:
      <PayrollCycle cycle={payrollCycle} />
    </div>
    <KeyValueTable
      data={{
        'Maximum instalment': maxInstalment,
        'Maximum issuable': maxIssuable
      }}
      containsKeyValueTableObjects={true}
      currencyValues={true}
      highlight={payrollCycle
        ? payrollCycle.toLowerCase()
        : null
      }
      keyColumnSize='7-of-12'
    />
  </div>
)

const PayrollCycle = ({ cycle }) => (
  cycle &&
    <span className='slds-font-size-smaller slds-text-color_weak slds-text-body_small'>
      &ensp;
      {`(${cycle.toLowerCase()} payroll cycle)`}
    </span>
)

const EmployerRules = ({ rules }) => (
  <React.Fragment>
    <div className='u-font-size-larger u-font-weight-bold slds-m-top_medium'>
      Employer loans rules:
    </div>
    <div className='slds-p-left_large slds-m-bottom_medium'>
      {rules && rules.length > 0
        ? rules
        : 'none specified'
      }
    </div>
  </React.Fragment>
)

class LoanEligibility extends React.PureComponent {
  // getDerivedStateFromProps is invoked right before calling the render method, both on the initial mount and on subsequent updates.
  // It should return an object to update the state, or null to update nothing.
  static getDerivedStateFromProps(props, state) {
    if (!state.overrideInitiated) {
      let newState = {}
      if (props.cashEligibleFor !== state.cashEligibleFor) {
        newState.cashEligibleFor = props.cashEligibleFor
      }
      if (props.retailEligibleFor !== state.retailEligibleFor) {
        newState.retailEligibleFor = props.retailEligibleFor
      }
      if (props.consolidationEligibleFor !== state.consolidationEligibleFor) {
        newState.consolidationEligibleFor = props.consolidationEligibleFor
      }
      if (Object.keys(newState).length > 0) {
        return newState
      } else {
        return null
      }
    } else {
      return null
    }
  }

  state = {
    cashEligibleFor: this.props.cashEligibleFor,
    retailEligibleFor: this.props.retailEligibleFor,
    consolidationEligibleFor: this.props.consolidationEligibleFor,
    cashAppliedForEligible: null,
    retailAppliedForEligible: null,
    consolidationAppliedForEligible: null,
    overrideReason: '',
    overrideInitiated: false,
    savingOverride: false
  }

  performCalculation = () => {
    let data = new FormData()
    data.append('loanapplication_id', JSON.stringify(this.props.loanApplication))

    fetch(`${process.env.REACT_APP_API_ROOT}/v1/loanapplication/recalculate`,
    {
      method: "POST",
      headers: {
        Authorization: "Token " + localStorage.getItem('token') || null
      },
      body: data
    })
    .then((res) => res.json())
    .then((data) => {
      if(data.success) {
        console.log('recalculation success')
        this.props.refetchData()
      } else {
        console.log(`recalculation failed - ${data.failure}`)
      }
    })
  }

  hasEligibleLoanAmountBeenAppliedFor = () => (
    this.state.cashAppliedForEligible ||
    this.state.retailAppliedForEligible ||
    this.state.consolidationAppliedForEligible
  )

  hasEligibleForFieldBeenChanged = () => (
    this.state.cashEligibleFor !== this.props.cashEligibleFor ||
    this.state.retailEligibleFor !== this.props.retailEligibleFor ||
    this.state.consolidationEligibleFor !== this.props.consolidationEligibleFor
  )

  hasEligibleForAmount = () => (
    this.state.cashEligibleFor || this.state.retailEligibleFor || this.state.consolidationEligibleFor
  )

  saveOverride = () => {
    let params = {}
    if (this.hasEligibleForFieldBeenChanged()) {
      params.overrideAt = moment.utc().format("YYYY-MM-DD HH:mm:ss.SSS");
      params.overrideBy = localStorage.getItem('userUUID')
      if (this.state.cashEligibleFor !== this.props.cashEligibleFor) {
        params.cashEligibleFor = this.state.cashEligibleFor
      }
      if (this.state.retailEligibleFor !== this.props.retailEligibleFor) {
        params.retailEligibleFor = this.state.retailEligibleFor
      }
      if (this.state.consolidationEligibleFor !== this.props.consolidationEligibleFor) {
        params.consolidationEligibleFor = this.state.consolidationEligibleFor
      }
    }
    if (this.state.overrideReason) {
      params.overrideNotes = this.state.overrideReason
    }
    if (Object.keys(params).length > 0) {
      this.setState({ savingOverride: true })
      this.props.updateLoanApplication(params)
      .then(() => this.setState({ savingOverride: false, overrideInitiated: false }))
      .catch(() => this.setState({ savingOverride: false }))
    }
  }

  render() {
    let userHasOverridePermission = false  // changed from hasResponsibility('self', 'loanapplication_override')
    let eligibleForFieldChanged = this.hasEligibleForFieldBeenChanged()

    return (
      <div className='slds-m-top_small slds-m-bottom_large'>
        <div className='u-font-size-larger u-font-weight-bold slds-m-bottom_small'>
          Eligibility:
        </div>
        <Table
          columns={[ 'loan type', 'eligible for' ]}
          rows={[
            [
              'Cash',
              <DisplayEligibleFor
                value={this.state.cashEligibleFor}
                override={this.state.overrideInitiated}
                onChange={e => this.setState({ cashEligibleFor: parseFloat(e.target.value) })}
              />
            ],
            [
              'Retail',
              <DisplayEligibleFor
                value={this.state.retailEligibleFor}
                override={this.state.overrideInitiated}
                onChange={e => this.setState({ retailEligibleFor: parseFloat(e.target.value) })}
              />
            ],
            [
              'Consolidation',
              <DisplayEligibleFor
                value={this.state.consolidationEligibleFor}
                override={this.state.overrideInitiated}
                onChange={e => this.setState({ consolidationEligibleFor: parseFloat(e.target.value) })}
              />
            ]
          ]}
        />
        {this.props.overrideAt &&
          <div className='slds-m-top_small slds-m-bottom_small slds-text-color_weak'>
            <SvgIcon
              variant='utility'
              symbol='info'
              colour='light'
              size='x-small'
              className='slds-m-right_xx-small'
            />
            {`Override processed by ${this.props.overrideByName} at ${moment(this.props.overrideAt).format('lll')}`}
          </div>
        }
        <div className='slds-grid slds-wrap slds-p-top_small slds-p-bottom_small'>
          {eligibleForFieldChanged && this.state.overrideInitiated &&
            <div className='slds-col slds-size_1-of-1'>
              <div className='slds-grid'>
                <SvgIcon
                  variant='utility'
                  symbol='replace'
                  colour='warning'
                  size='x-small'
                />
                <TextArea
                  label="Reason for overriding 'eligible for' field(s)?"
                  value={this.state.overrideReason}
                  onChange={e => this.setState({ overrideReason: e.target.value })}
                  className='slds-m-left_large slds-size_3-of-4'
                />
              </div>
            </div>
          }
          {this.state.overrideInitiated
            ? <div className='slds-col_bump-left'>
                <Button
                  disabled={this.state.savingOverride}
                  onClick={() => this.hasEligibleForFieldBeenChanged
                    ? this.setState({
                        overrideInitiated: false,
                        cashEligibleFor: this.props.cashEligibleFor,
                        retailEligibleFor: this.props.retailEligibleFor,
                        consolidationEligibleFor: this.props.consolidationEligibleFor
                      })
                    : this.setState({ overrideInitiated: false })
                  }>
                  Cancel override
                </Button>
                <Button
                  type={this.state.overrideInitiated ? 'brand' : null}
                  onClick={this.saveOverride}
                  maxHeight={32}
                  disabled={this.state.savingOverride || !this.hasEligibleForFieldBeenChanged() || !this.state.overrideReason}>
                  {this.state.savingOverride ? 'Saving...' : 'Save override'}
                </Button>
              </div>
            : <div className='slds-col_bump-left'>
                {userHasOverridePermission &&
                  <Button
                    type={this.state.overrideInitiated ? 'brand' : null}
                    onClick={() => this.setState({ overrideInitiated: true })}
                    maxHeight={32}>
                    Initiate override
                  </Button>
                }
                <ReadOnlyContext.Consumer>
                  {readOnly => (
                    <Button
                      onClick={this.performCalculation}
                      disabled={readOnly}>
                      {this.hasEligibleForAmount() ? 'Recalculate' : 'Calculate'}
                    </Button>
                  )}
                </ReadOnlyContext.Consumer>
              </div>
          }
        </div>
      </div>
    )
  }
}

const DisplayEligibleFor = ({ value, override, onChange }) => (
  override
    ? <CurrencyInput
        value={value}
        onChange={onChange}
        maxWidth={100}
      />
    : value ? showAsCurrency('R', value) : 'n/a'
)



// WEBPACK FOOTER //
// ./src/components/loanapplication-calculations/index.js